import { useMemo, useCallback } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTicketingModalRootActions } from '@/features/ticketings/_shared/components/ticketing-modal-root/ticketing-modal-root.store';
import { FileWithPreview } from '@/features/_shared/hooks/use-file-upload';
import { useGetListColumnQuery } from '@/features/ticketings/columns/column/api/get-list-column.api';
import { useDownloadCreateBulkTicketTemplateMutation } from "@/features/ticketing/tickets/api/download-create-bulk-ticket-template.api";
import {
  CreateBulkTicketValidationSchema,
  TCreateBulkTicketValidation,
  useCreateBulkTicketMutation,
} from '@/features/ticketing/tickets/api/create-bulk-ticket.api';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { axiosExtractErrorMessage, AxiosErrorCustom } from '@/lib/axios';

export interface ICreateBulkTicketModalProps {
  spaceId: string;
}

export function useCreateBulkTicketModal({ spaceId }: ICreateBulkTicketModalProps) {
  // ===== Hooks =====
  const modalActions = useTicketingModalRootActions();
  const showToast = useCustomToast();

  // ===== Form =====
  const form = useForm<TCreateBulkTicketValidation>({
    resolver: zodResolver(CreateBulkTicketValidationSchema),
    mode: 'onChange',
    defaultValues: {
      spaceColumnId: '',
      file: undefined as unknown as File,
    },
  });

  // Watch file from form
  const watchedFile = useWatch({ control: form.control, name: 'file' });

  // ===== Handlers =====
  const handleCloseModal = useCallback(() => {
    modalActions.closeModal('CreateBulkTicketModal');
    form.reset();
  }, [modalActions, form]);

  // ===== Mutations =====
  const { mutate: createBulkTicket, isPending: isUploading } = useCreateBulkTicketMutation({
    mutationConfig: {
      onSuccess: () => {
        showToast({ type: 'success', message: 'Bulk tickets created successfully!' });
        handleCloseModal();
      },
      onError: (error) => {
        const errorMessage = axiosExtractErrorMessage(error as AxiosErrorCustom);
        showToast({ type: 'error', message: `Failed to create bulk tickets: ${errorMessage}` });
      },
    },
  });
  const { mutate: downloadCreateBulkTicketTemplate, isPending: isDownloadingCreateBulkTicketTemplate } = useDownloadCreateBulkTicketTemplateMutation({
    mutationConfig: {
      onSuccess: (data) => {
        // When responseType is 'blob', axios returns the blob directly in response.data
        // The ApiResponse wrapper may still apply, so we check for payload or use data directly
        let blob: Blob | undefined;
        if (data instanceof Blob) {
          blob = data;
        } else if (data && typeof data === 'object' && 'payload' in data && data.payload instanceof Blob) {
          blob = data.payload;
        } else if (data && typeof data === 'object' && 'data' in data && data.data instanceof Blob) {
          blob = data.data;
        }

        if (blob) {
          const BACKUP_FILE_NAME = 'create-bulk-ticket-template.xlsx';
          const SERVER_FILE_NAME = data?.headers?.['content-disposition']?.match(/filename="(.+)"/)?.[1] || BACKUP_FILE_NAME;     
          const URL = window.URL.createObjectURL(blob);
          const A = document.createElement('a');
          A.href = URL;
          A.download = SERVER_FILE_NAME;
          A.click();
          window.URL.revokeObjectURL(URL);
        } else {
          showToast({ type: 'error', message: 'Failed to download template: Invalid file data' });
        }
      },
      onError: (error) => {
        const errorMessage = axiosExtractErrorMessage(error as AxiosErrorCustom);
        showToast({ type: 'error', message: `Failed to download template: ${errorMessage}` });
      },
    },
  });

  // ===== Data Queries =====
  // Space Columns
  const { data: listSpaceColumnsData, isLoading: isLoadingListSpaceColumnsData } = useGetListColumnQuery({
    params: { spaceId: spaceId || '', page: 1, limit: 500 },
    queryConfig: { enabled: !!spaceId },
    select: (data) => data.payload || [],
  });

  // ===== Modal State =====
  const isCreateBulkTicketOpen = useMemo(() => {
    return modalActions.checkIsOpenModal('CreateBulkTicketModal');
  }, [modalActions]);

  const hasFile = useMemo(() => {
    return watchedFile instanceof File;
  }, [watchedFile]);

  const handleDownloadTemplate = useCallback(() => {
    downloadCreateBulkTicketTemplate({ spaceId });
  }, [downloadCreateBulkTicketTemplate, spaceId]);

  const handleFileSelect = useCallback(
    (files: FileWithPreview[]) => {
      if (files.length > 0 && files[0]?.file instanceof File) {
        form.setValue('file', files[0].file, { shouldValidate: true });
      }
    },
    [form]
  );

  const handleFileRemove = useCallback(() => {
    form.resetField('file');
  }, [form]);

  const handleSubmit = useCallback(
    (data: TCreateBulkTicketValidation) => {
      createBulkTicket({
        file: data.file,
        spaceColumnId: data.spaceColumnId,
      });
    },
    [createBulkTicket]
  );

  

  return {
    // Modal
    isCreateBulkTicketOpen,
    handleCloseModal,
    hasFile,

    // Form
    form,

    // File
    selectedFile: watchedFile instanceof File ? { file: watchedFile, id: '', isFile: true } : null,
    handleFileSelect,
    handleFileRemove,

    // Data
    listSpaceColumnsOptions: listSpaceColumnsData || [],
    isLoadingListSpaceColumnsData,

    // Actions
    handleDownloadTemplate,
    handleSubmit,
    isUploading,

    // Download Template
    downloadCreateBulkTicketTemplate,
    isDownloadingCreateBulkTicketTemplate,
  };
}
